<template>
    <div class="vmc-forgot-password d-flex align-items-center justify-content-center">
        <div>
            <div class="password-box px-4 py-4 bg-white d-flex flex-column align-items-center justify-content-center">
                <div class="password-container d-flex align-items-center justify-content-center mb-4">
                    <img class="logo-image" :src="vmcLogo" alt="">
                    <div class="logo-desc">
                        <p class="mb-0 text-center vmc-text-primary" id="VMCMeaning">VERITAS MARITIME CORPORATION</p>
                        <p class="mb-0 text-center" id="VMCTag">QUALITY SERVICE, QUALITY CREW</p>
                    </div>
                </div>

                <!-- STEP 1: CREDENTIAL INPUT -->
                <form v-if="step === 1" @submit.prevent="handleSendEmail" class="w-100 d-flex flex-column align-items-center">
                    <h2 class="text-dark">Reset Password</h2>

                    <div class="vmc-input-box mt-4 mb-3 position-relative">
                        <span class="icon position-absolute"><i class="text-dark bi bi-person-fill"></i></span>
                        <input v-model="username" class="text-dark w-100 bg-transparent border-0" type="text" required>
                        <label class="position-absolute">Username</label>
                    </div>

                    <div class="vmc-input-box mt-1 mb-4 position-relative">
                        <span class="icon position-absolute"><i class="text-dark bi bi-envelope-fill"></i></span>
                        <input v-model="email" class="text-dark w-100 bg-transparent border-0" type="email" required>
                        <label class="position-absolute">Email Address</label>
                    </div>

                    <p v-if="errorMessage" class="text-danger text-center mb-3" style="font-size:0.85rem;">
                        {{ errorMessage }}
                    </p>

                    <div class="mb-3 w-100" style="max-width: 310px;">
                        <button type="submit" class="text-white vmc-bg-prime-4 border-0 px-2 py-2 rounded w-100" :disabled="loading">
                            {{ loading ? 'Checking...' : 'Send Code' }}
                        </button>
                    </div>
                    <router-link :to="{ name: 'LoginPage' }" class="loginpage">Back to Login</router-link>
                </form>

                <!-- STEP 2: OTP VERIFICATION -->
                <form v-if="step === 2" @submit.prevent="handleVerifyOtp" class="w-100 d-flex flex-column align-items-center">
                    <h2 class="text-dark">Enter OTP</h2>
                    <div class="text-center my-3">
                        <p class="text-dark mb-0">We sent a verification code to</p>
                        <strong class="text-dark">{{ email }}</strong>
                    </div>

                    <div class="d-flex align-items-center justify-content-center my-3" style="gap:10px;">
                        <input v-for="(digit, index) in otp" :key="index" v-model="otp[index]"
                            @input="handleInput(index)" @keydown.backspace="handleBackspace(index)" ref="otpInputs"
                            type="text" class="otp-input text-center border-0" maxlength="1" required>
                    </div>

                    <p v-if="errorMessage" class="text-danger text-center mb-3" style="font-size:0.85rem;">
                        {{ errorMessage }}
                    </p>

                    <div class="mt-3 mb-3 w-100" style="max-width: 310px;">
                        <button type="submit" class="verify-otp text-white vmc-bg-prime-4 border-0 px-2 py-2 rounded w-100" :disabled="loading">
                            {{ loading ? 'Verifying...' : 'Verify OTP' }}
                        </button>
                    </div>
                    <div class="text-center w-100">
                        <button type="button" @click="step = 1; errorMessage = '';" class="border-0 bg-transparent loginpage p-0 mt-2">
                            Go back
                        </button>
                    </div>
                </form>

                <!-- STEP 3: NEW PASSWORD -->
                <form v-if="step === 3" @submit.prevent="handleResetPassword" class="w-100 d-flex flex-column align-items-center">
                    <h2 class="text-dark mb-2">Reset Password</h2>

                    <div class="vmc-input-box my-4 position-relative mt-5">
                        <span class="icon position-absolute" @click="toggleNewPassword">
                            <i v-if="showNewPassword" class="text-dark bi bi-eye-fill"></i>
                            <i v-else class="text-dark bi bi-eye-slash-fill"></i>
                        </span>
                        <input v-model="newPassword" class="text-dark w-100 bg-transparent border-0"
                            :type="showNewPassword ? 'text' : 'password'" required>
                        <label class="position-absolute">New Password</label>
                    </div>

                    <div class="vmc-input-box mt-1 mb-4 position-relative">
                        <span class="icon position-absolute" @click="toggleConfirmNewPassword">
                            <i v-if="showConfirmNewPassword" class="text-dark bi bi-eye-fill"></i>
                            <i v-else class="text-dark bi bi-eye-slash-fill"></i>
                        </span>
                        <input v-model="confirmPassword" class="text-dark w-100 bg-transparent border-0"
                            :type="showConfirmNewPassword ? 'text' : 'password'" required>
                        <label class="position-absolute">Confirm New Password</label>
                    </div>

                    <p v-if="errorMessage" class="text-danger text-center mb-3" style="font-size:0.85rem;">
                        {{ errorMessage }}
                    </p>

                    <p v-if="successMessage" class="text-success text-center mb-3" style="font-size:0.85rem;">
                        {{ successMessage }}
                    </p>

                    <!-- FIXED: disabled only when loading or after success -->
                    <div class="mb-3 w-100" style="max-width: 310px;">
                        <button type="submit"
                            class="text-white vmc-bg-prime-4 px-2 py-2 rounded w-100 vmc-set-password-btn"
                            :disabled="loading || !!successMessage">
                            <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {{ loading ? 'Saving...' : 'Set Password' }}
                        </button>
                    </div>

                </form>

            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ForgotPassword",
    data() {
        return {
            vmcLogo: require(`@/assets/veritas_logo.png`),
            step: 1,
            username: '',
            email: '',
            otp: ["", "", "", "", "", ""],
            newPassword: '',
            confirmPassword: '',
            resetToken: '',
            errorMessage: '',
            successMessage: '',
            loading: false,
            showNewPassword: false,
            showConfirmNewPassword: false
        }
    },
    methods: {
        async handleSendEmail() {
            if (!this.username || !this.email) return;
            this.loading = true;
            this.errorMessage = '';
            try {
                const response = await fetch(`${process.env.VUE_APP_API_URL}/api/verify-user`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: this.username, email: this.email })
                });
                const data = await response.json();
                if (!response.ok) {
                    this.errorMessage = data.message || "Invalid credentials.";
                    return;
                }
                this.step = 2;
            } catch (error) {
                this.errorMessage = "Server error.";
            } finally {
                this.loading = false;
            }
        },

        async handleVerifyOtp() {
            const code = this.otp.join('');
            if (code.length !== 6) return;
            this.loading = true;
            this.errorMessage = '';
            try {
                const response = await fetch(`${process.env.VUE_APP_API_URL}/api/verify-otp`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: this.email, username: this.username, otp: code })
                });
                const data = await response.json();
                if (!response.ok) {
                    this.errorMessage = data.message || "Invalid OTP.";
                    return;
                }
                this.resetToken = data.resetToken;
                this.step = 3;
            } catch (error) {
                this.errorMessage = "Server error.";
            } finally {
                this.loading = false;
            }
        },

        async handleResetPassword() {
            this.errorMessage = '';

            // VALIDATION — check empty fields first
            if (!this.newPassword || !this.confirmPassword) {
                this.errorMessage = "Please fill in both password fields.";
                return;
            }

            // VALIDATION — check passwords match
            if (this.newPassword !== this.confirmPassword) {
                this.errorMessage = "Passwords do not match.";
                return;
            }

            this.loading = true;
            try {
                const response = await fetch(`${process.env.VUE_APP_API_URL}/api/reset-password`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ resetToken: this.resetToken, newPassword: this.newPassword })
                });
                const data = await response.json();
                if (!response.ok) {
                    this.errorMessage = data.message || "Failed to reset password.";
                    return;
                }
                this.successMessage = "Password successfully changed. Redirecting to login...";
                    setTimeout(() => {
                        this.$router.push({ name: 'LoginPage' });
                    }, 3000);
            } catch (error) {
                this.errorMessage = "Server error. Please check your connection or contact support.";
                console.error('Reset password error:', error);
            } finally {
                this.loading = false;
            }
        },

        toggleNewPassword()        { this.showNewPassword = !this.showNewPassword; },
        toggleConfirmNewPassword() { this.showConfirmNewPassword = !this.showConfirmNewPassword; },

        handleInput(index) {
            if (this.otp[index].length === 1 && index < this.otp.length - 1) {
                this.$refs.otpInputs[index + 1].focus();
            }
        },

        handleBackspace(index) {
            if (!this.otp[index] && index > 0) {
                this.$refs.otpInputs[index - 1].focus();
            }
        }
    }
}
</script>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.vmc-forgot-password {
    min-height: 100vh;
    background-image: url('/vessel_cape_sasanqua.jpg');
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    background-attachment: fixed;
}

.vmc-forgot-password::before {
    content: "";
    position: fixed;
    inset: 0;
    background-color: rgba(8, 98, 165, 0.4);
    z-index: 1;
}

.vmc-forgot-password > div {
    position: relative;
    z-index: 2;
}

.password-container {
    font-weight: 700;
}

#VMCTag {
    color: var(--vmc-primary-3);
}

.password-box {
    margin-top: 10px;
    position: relative;
    max-width: 100%;
    width: auto;
    height: auto;
    border: 2px solid var(--vmc-dark-2);
    border-radius: 20px;
    box-shadow: 0 0 15px 0;
}

h2 {
    font-size: 24px;
    text-align: center;
}

.vmc-input-box {
    position: relative;
    width: 310px;
    max-width: 100%;
    border-bottom: 1px solid #000;
}

input { outline: none; }

.vmc-input-box label {
    position: absolute;
    top: 50%;
    left: 2px;
    transform: translateY(-50%);
    font-size: 1em;
    pointer-events: none;
    transition: 0.3s;
}

.vmc-input-box input:focus ~ label,
.vmc-input-box input:valid ~ label {
    top: -5px;
    font-size: 12px;
}

.vmc-input-box input {
    height: 50px;
    font-size: 14px;
}

.vmc-input-box .icon {
    position: absolute;
    right: 8px;
    font-size: 1.2em;
    line-height: 50px;
}

.otp-input {
    background-color: rgb(228, 228, 228);
    width: 43px;
    height: 43px;
    border-radius: 7px;
    color: rgb(44, 44, 44);
    outline: none;
    font-weight: 600;
}

a span { transition: 0.3s; color: var(--vmc-primary-4); }
a:hover span { color: var(--vmc-primary-5); text-decoration: underline; }
div button { transition: 0.3s; }
div button:hover:not(:disabled) { background-color: var(--vmc-primary-5); }

.loginpage {
    color: var(--vmc-primary-4);
    font-weight: 700;
    transition: 0.3s;
    text-decoration: none;
}

.loginpage:hover {
    text-decoration: underline;
    color: var(--vmc-primary-5);
}

@media (min-width: 330px) {
    .vmc-forgot-password { overflow-y: hidden; }
    .password-box { padding: 20px 0; }
    .logo-image { width: 60px; }
    #VMCMeaning, #VMCTag { font-size: 14px; }
}

@media (min-width: 768px) {
    .logo-image { width: 70px; }
    #VMCMeaning, #VMCTag { font-size: 16px; }
}

.vmc-set-password-btn {
    cursor: pointer;
    border: none;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(8,98,165,0.08);
    transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}

.vmc-set-password-btn:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    background: #b0b0b0 !important;
    color: #fff !important;
}

.vmc-set-password-btn:not(:disabled):hover {
    background-color: var(--vmc-primary-5);
    color: #fff;
    box-shadow: 0 4px 16px rgba(8,98,165,0.15);
}
</style>